import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useClinicContext } from './useClinicContext';
import {
  createCheckinMail,
  deleteCheckinMail,
  fetchCheckinMailOne,
  updateCheckinMail,
  type CheckinMailPayload,
} from '@/services/main/checkInMain/checkinMailService';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';

export function useCheckinMailOne(patientId: string | undefined, id: string | undefined) {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['checkin-mails', 'one', tenant, clinicNumber, patientId, id],
    queryFn: () => fetchCheckinMailOne(tenant, clinicNumber, patientId!, id!),
    enabled: !!tenant && !!clinicNumber && !!patientId && !!id && id !== 'new',
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isError, refetch };
}

export function useCheckinMailCreate(patientId: string) {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CheckinMailPayload) => createCheckinMail(tenant, clinicNumber, patientId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checkin-mails'] });
    },
  });
}

export function useCheckinMailUpdate(patientId: string) {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CheckinMailPayload }) =>
      updateCheckinMail(tenant, clinicNumber, patientId, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checkin-mails'] });
    },
  });
}

export function useCheckinMailDelete(patientId: string) {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();
  const toast = useAppToast();

  return useMutation({
    mutationFn: (id: number) => deleteCheckinMail(tenant, clinicNumber, patientId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checkin-mails'] });
      toast.success('削除が正常に終了しました。');
    },
    onError: err => {
      toast.error(getApiErrorMessage(err, '削除ができませんでした。'));
    },
  });
}

